import { useEffect, useState } from 'react';
import {
  FiX, FiFileText, FiUser, FiUsers, FiAlertCircle,
  FiCheckCircle, FiInfo, FiChevronUp, FiChevronDown, FiPlus,
  FiTrendingUp, FiTag, FiTrash2, FiBell,
} from 'react-icons/fi';
import { userRequest } from '../../utils/requestMethods';
import { generateSchoolSessions } from '../../utils/schoolYearUtils';
import ToggleSwitch from '../settings/ToggleSwitch';
import ButtonLoading from '../ButtonLoading';

const schoolSessions = generateSchoolSessions(2020, 15);

const SectionHeader = ({ icon: Icon, children }) => (
  <div className="flex items-center gap-1.5">
    <Icon className="text-gray-400" size={13} />
    <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">{children}</p>
  </div>
);

const GenerateInvoicesModal = ({
  closeModal, onGenerate, schoolId, schoolCode, classId,
  alert, student, currentSession, currentTerm, classesAhead = [],
  submitting = false,
}) => {
  const [addons, setAddons] = useState([]);
  const [transportAddons, setTransportAddons] = useState([]);
  const [loading, setLoading] = useState(true);

  // ordered list of selected addon ids — position = priority
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [transportAddOn, setTransportAddOn] = useState('');
  const [session, setSession] = useState(currentSession || '');
  const [term, setTerm] = useState(currentTerm ? String(currentTerm) : '');

  // Billing class override — bill at an intended (not-yet-promoted-to) class fee
  const [useBillingClass, setUseBillingClass] = useState(false);
  const [billingClassId, setBillingClassId] = useState('');

  // Arrears — extra one-off line items to add to this invoice
  const [arrears, setArrears] = useState([]);
  const [arrearDraft, setArrearDraft] = useState({ name: '', description: '', amount: '' });
  const [showArrearForm, setShowArrearForm] = useState(false);

  const [notifyParents, setNotifyParents] = useState(true);

  const isSingleMode = Boolean(student);
  const studentName = student ? `${student.firstname} ${student.lastname}`.trim() : null;

  // The class whose extra fees should be offered — the intended (billing-override)
  // class when one's selected, otherwise the student's actual current class.
  const effectiveClassId = useBillingClass && billingClassId ? billingClassId : classId;
  const billingClassName = classesAhead.find((c) => c._id === billingClassId)?.class_name;

  useEffect(() => {
    const fetchAddons = async () => {
      if (!schoolId || !effectiveClassId) return;
      try {
        setLoading(true);
        const [addonsRes, transportRes] = await Promise.all([
          userRequest.get(`class-addon/${schoolId}/${effectiveClassId}`),
          userRequest.get(`transport-addon?code=${schoolCode}`),
        ]);
        setAddons((addonsRes.data.add_on || []).filter((a) => a?._id));
        setTransportAddons(transportRes.data.transport_add_on || []);
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };
    fetchAddons();
  }, [effectiveClassId, schoolId, schoolCode]);

  // Extra fees are class-specific — a previously selected add-on's id may not
  // exist on the newly billed class, so clear the selection when it changes.
  useEffect(() => {
    setSelectedAddons([]);
  }, [effectiveClassId]);

  const addAddon = (id) => {
    if (!selectedAddons.includes(id)) setSelectedAddons((prev) => [...prev, id]);
  };

  const removeAddon = (id) => {
    setSelectedAddons((prev) => prev.filter((a) => a !== id));
  };

  const moveUp = (index) => {
    if (index === 0) return;
    setSelectedAddons((prev) => {
      const next = [...prev];
      [next[index - 1], next[index]] = [next[index], next[index - 1]];
      return next;
    });
  };

  const moveDown = (index) => {
    setSelectedAddons((prev) => {
      if (index === prev.length - 1) return prev;
      const next = [...prev];
      [next[index], next[index + 1]] = [next[index + 1], next[index]];
      return next;
    });
  };

  const buildAddOnsPayload = () =>
    selectedAddons.map((id, i) => ({ addon_id: id, priority: i + 1 }));

  const addArrear = () => {
    if (!arrearDraft.name.trim() || !arrearDraft.amount) return;
    setArrears((prev) => [...prev, { ...arrearDraft, name: arrearDraft.name.trim() }]);
    setArrearDraft({ name: '', description: '', amount: '' });
    setShowArrearForm(false);
  };

  const removeArrear = (index) => {
    setArrears((prev) => prev.filter((_, i) => i !== index));
  };

  const arrearsTotal = arrears.reduce((sum, a) => sum + (Number(a.amount) || 0), 0);

  const toggleBillingClass = (checked) => {
    setUseBillingClass(checked);
    if (!checked) setBillingClassId('');
  };

  const handleGenerate = () => {
    onGenerate({
      addOns: buildAddOnsPayload(),
      transportAddOn: transportAddOn || '',
      session,
      term: term ? Number(term) : undefined,
      billingClassId: useBillingClass && billingClassId ? billingClassId : undefined,
      arrears: arrears.length > 0 ? arrears : undefined,
      notifyParents,
    });
  };

  const availableAddons = addons.filter((a) => !selectedAddons.includes(a._id));
  const addonById = Object.fromEntries(addons.map((a) => [a._id, a]));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[92vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-50 rounded-xl">
              <FiFileText className="text-indigo-600 text-lg" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">Generate Invoice</h2>
              <p className="text-xs text-gray-400">
                {isSingleMode ? `For ${studentName}` : 'All students in this class'}
              </p>
            </div>
          </div>
          <button onClick={closeModal} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 overflow-y-auto space-y-6 flex-1">

          {/* Target */}
          <div className={`flex items-center gap-3 rounded-xl border px-4 py-3 ${isSingleMode ? 'bg-blue-50 border-blue-100' : 'bg-gray-50 border-gray-200'}`}>
            {isSingleMode
              ? <FiUser className="text-blue-500 text-xl shrink-0" />
              : <FiUsers className="text-gray-500 text-xl shrink-0" />
            }
            <p className="text-sm font-medium text-gray-800">
              {isSingleMode ? studentName : 'All students in class'}
            </p>
          </div>

          {/* Alert */}
          {alert?.message && (
            <div className={`flex items-start gap-3 rounded-xl px-4 py-3 text-sm border ${alert.type === 'success' ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'}`}>
              {alert.type === 'success' ? <FiCheckCircle className="mt-0.5 shrink-0" /> : <FiAlertCircle className="mt-0.5 shrink-0" />}
              <span>{alert.message}</span>
            </div>
          )}

          {/* Session & Term */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Session</label>
              <select
                value={session}
                onChange={(e) => setSession(e.target.value)}
                className="w-full px-3.5 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 focus:bg-white"
              >
                <option value="">Select session</option>
                {schoolSessions.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Term</label>
              <select
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                className="w-full px-3.5 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 focus:bg-white"
              >
                <option value="">Select term</option>
                <option value="1">Term 1</option>
                <option value="2">Term 2</option>
                <option value="3">Term 3</option>
              </select>
            </div>
          </div>

          {/* Billing class override — for students due for promotion but not yet moved */}
          <div className="space-y-2.5">
            <SectionHeader icon={FiTrendingUp}>Billing Class</SectionHeader>

            {term === '3' && !useBillingClass && (
              <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-100 rounded-xl text-xs text-amber-800">
                <FiAlertCircle size={13} className="mt-0.5 shrink-0 text-amber-500" />
                <p>
                  If {isSingleMode ? 'this student is' : 'any students here are'} due for promotion, either promote{isSingleMode ? '' : ' them'} first,
                  or turn on the option below to bill at the intended class's fee instead.
                </p>
              </div>
            )}

            <label className="flex items-center gap-3 px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl cursor-pointer">
              <input
                type="checkbox"
                checked={useBillingClass}
                onChange={(e) => toggleBillingClass(e.target.checked)}
                disabled={classesAhead.length === 0}
                className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-400 disabled:opacity-50"
              />
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-700">
                  {isSingleMode ? 'Bill at intended (upcoming) class fee' : "Bill using students' intended class fee"}
                </p>
                <p className="text-xs text-gray-400">
                  {classesAhead.length === 0
                    ? 'No class configured ahead of this one.'
                    : "Charges the selected class's fee instead of this class's — the student isn't moved."}
                </p>
              </div>
            </label>

            {useBillingClass && (
              <div className="pl-1">
                <select
                  value={billingClassId}
                  onChange={(e) => setBillingClassId(e.target.value)}
                  className="w-full px-3.5 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 focus:bg-white"
                >
                  <option value="">Select intended class</option>
                  {classesAhead.map((c) => (
                    <option key={c._id} value={c._id}>{c.class_name}</option>
                  ))}
                </select>
                {billingClassId && (
                  <div className="flex items-start gap-2 mt-2 p-3 bg-amber-50 border border-amber-100 rounded-xl text-xs text-amber-800">
                    <FiInfo size={13} className="mt-0.5 shrink-0 text-amber-500" />
                    <p>Double-check that fees for this class are up to date before generating — the invoice will charge exactly what's configured there.</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Add-ons */}
          {!loading && addons.length > 0 && (
            <div className="space-y-3">
              <SectionHeader icon={FiTag}>Extra Fees</SectionHeader>

              {/* Explainer */}
              <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-100 rounded-xl text-xs text-amber-800">
                <FiInfo size={13} className="mt-0.5 shrink-0 text-amber-500" />
                <p>
                  Tap an add-on to include it in the invoice. Use the arrows to set the order — the first item has the highest priority.
                  {useBillingClass && billingClassId && billingClassName && (
                    <> Showing extra fees for <strong>{billingClassName}</strong> (the billing class), not the current class.</>
                  )}
                </p>
              </div>

              {/* Available addons */}
              {availableAddons.length > 0 && (
                <div className="space-y-1.5">
                  {availableAddons.map((addon) => (
                    <button
                      key={addon._id}
                      type="button"
                      onClick={() => addAddon(addon._id)}
                      className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl hover:bg-indigo-50 hover:border-indigo-200 transition-colors group"
                    >
                      <div className="text-left">
                        <p className="text-sm font-medium text-gray-700 group-hover:text-indigo-800">{addon.add_on_name}</p>
                        <p className="text-xs text-gray-400">₦{Number(addon.add_on_price).toLocaleString()}</p>
                      </div>
                      <div className="w-6 h-6 rounded-full bg-gray-200 group-hover:bg-indigo-500 flex items-center justify-center transition-colors">
                        <FiPlus size={13} className="text-gray-500 group-hover:text-white" />
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Selected addons (ordered) */}
              {selectedAddons.length > 0 && (
                <div className="space-y-1.5">
                  <p className="text-xs text-gray-500 font-medium">Added — drag to reorder priority</p>
                  {selectedAddons.map((id, index) => {
                    const addon = addonById[id];
                    if (!addon) return null;
                    return (
                      <div
                        key={id}
                        className="flex items-center gap-3 px-4 py-3 bg-indigo-50 border border-indigo-200 rounded-xl"
                      >
                        {/* Priority badge */}
                        <span className="w-6 h-6 rounded-full bg-indigo-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                          {index + 1}
                        </span>

                        {/* Addon info */}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-indigo-900">{addon.add_on_name}</p>
                          <p className="text-xs text-indigo-500">₦{Number(addon.add_on_price).toLocaleString()}</p>
                        </div>

                        {/* Reorder buttons */}
                        <div className="flex flex-col gap-1">
                          <button
                            type="button"
                            onClick={() => moveUp(index)}
                            disabled={index === 0}
                            className="w-7 h-7 rounded-full bg-white border border-indigo-200 flex items-center justify-center text-indigo-500 hover:bg-indigo-600 hover:text-white hover:border-indigo-600 disabled:opacity-25 disabled:cursor-not-allowed transition-all"
                          >
                            <FiChevronUp size={14} />
                          </button>
                          <button
                            type="button"
                            onClick={() => moveDown(index)}
                            disabled={index === selectedAddons.length - 1}
                            className="w-7 h-7 rounded-full bg-white border border-indigo-200 flex items-center justify-center text-indigo-500 hover:bg-indigo-600 hover:text-white hover:border-indigo-600 disabled:opacity-25 disabled:cursor-not-allowed transition-all"
                          >
                            <FiChevronDown size={14} />
                          </button>
                        </div>

                        {/* Remove */}
                        <button
                          type="button"
                          onClick={() => removeAddon(id)}
                          className="p-1 text-indigo-300 hover:text-red-500 transition-colors"
                        >
                          <FiX size={15} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {loading && (
            <p className="text-sm text-gray-400 text-center py-2">Loading extra fees...</p>
          )}

          {!loading && addons.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-2">
              No add-ons available for {useBillingClass && billingClassName ? billingClassName : 'this class'}.
            </p>
          )}

          {/* Transport */}
          {!loading && (
            <div>
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Transport Fee</label>
              <select
                value={transportAddOn}
                onChange={(e) => setTransportAddOn(e.target.value)}
                className="w-full px-3.5 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 focus:bg-white"
              >
                <option value="">None</option>
                {transportAddons.map((t) => (
                  <option key={t._id} value={t._id}>
                    {t.add_on_name} — ₦{Number(t.add_on_price).toLocaleString()}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Arrears */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <SectionHeader icon={FiTag}>Arrears</SectionHeader>
              {!showArrearForm && (
                <button
                  type="button"
                  onClick={() => setShowArrearForm(true)}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:text-indigo-800"
                >
                  <FiPlus size={13} /> Add Line Item
                </button>
              )}
            </div>

            {arrears.length === 0 && !showArrearForm && (
              <p className="text-xs text-gray-400">No outstanding balances added to this invoice.</p>
            )}

            {arrears.length > 0 && (
              <div className="space-y-1.5">
                {arrears.map((a, index) => (
                  <div key={index} className="flex items-center gap-3 px-4 py-3 bg-red-50 border border-red-100 rounded-xl">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-red-900">{a.name}</p>
                      {a.description && <p className="text-xs text-red-500">{a.description}</p>}
                    </div>
                    <p className="text-sm font-semibold text-red-700 shrink-0">₦{Number(a.amount).toLocaleString()}</p>
                    <button
                      type="button"
                      onClick={() => removeArrear(index)}
                      className="p-1 text-red-300 hover:text-red-600 transition-colors shrink-0"
                    >
                      <FiTrash2 size={14} />
                    </button>
                  </div>
                ))}
                <div className="flex items-center justify-between px-1 pt-1">
                  <p className="text-xs text-gray-500">Arrears total</p>
                  <p className="text-xs font-semibold text-gray-700">₦{arrearsTotal.toLocaleString()}</p>
                </div>
              </div>
            )}

            {showArrearForm && (
              <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl space-y-2.5">
                <input
                  type="text"
                  placeholder="Name (e.g. Previous term fees)"
                  value={arrearDraft.name}
                  onChange={(e) => setArrearDraft((d) => ({ ...d, name: e.target.value }))}
                  className="w-full px-3.5 py-2.5 text-sm bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400"
                />
                <input
                  type="text"
                  placeholder="Description (optional, e.g. 2025/2026 Term 1)"
                  value={arrearDraft.description}
                  onChange={(e) => setArrearDraft((d) => ({ ...d, description: e.target.value }))}
                  className="w-full px-3.5 py-2.5 text-sm bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400"
                />
                <input
                  type="number"
                  placeholder="Amount (₦)"
                  value={arrearDraft.amount}
                  onChange={(e) => setArrearDraft((d) => ({ ...d, amount: e.target.value }))}
                  className="w-full px-3.5 py-2.5 text-sm bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400"
                />
                <div className="flex items-center gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => { setShowArrearForm(false); setArrearDraft({ name: '', description: '', amount: '' }); }}
                    className="flex-1 px-4 py-2 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-full hover:bg-gray-100 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={addArrear}
                    disabled={!arrearDraft.name.trim() || !arrearDraft.amount}
                    className="flex-1 px-4 py-2 text-xs font-medium text-white bg-indigo-600 rounded-full hover:bg-indigo-700 disabled:bg-indigo-300 disabled:cursor-not-allowed transition-colors"
                  >
                    Add
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Notify parents */}
          <div className="pt-1 border-t border-gray-100">
            <div className="flex items-center gap-2 mt-3 mb-1">
              <FiBell className="text-gray-400" size={13} />
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Notifications</p>
            </div>
            <ToggleSwitch
              label="Notify parents by email & push notification"
              initialState={notifyParents}
              onChange={setNotifyParents}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 shrink-0">
          <div className="text-xs text-gray-400 space-y-0.5">
            <p>
              {selectedAddons.length > 0
                ? `${selectedAddons.length} add-on${selectedAddons.length > 1 ? 's' : ''}`
                : 'No add-ons'}
              {arrears.length > 0 && ` · ${arrears.length} arrear${arrears.length > 1 ? 's' : ''}`}
              {useBillingClass && billingClassId && ' · billing class override'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button type="button" onClick={closeModal} disabled={loading || submitting} className="px-5 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-full hover:bg-gray-50 transition-colors disabled:opacity-50">
              Cancel
            </button>
            {submitting ? (
              <ButtonLoading styles="px-5 py-2 text-sm font-medium text-white bg-indigo-500 rounded-full" />
            ) : (
              <button
                onClick={handleGenerate}
                disabled={loading || !session || !term}
                className="px-5 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-full transition-colors disabled:bg-indigo-300 disabled:cursor-not-allowed"
              >
                Generate Invoice
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GenerateInvoicesModal;
